// @ts-check
import {
  cloneDeep,
  filter,
  forEach,
  get,
  isEqual,
  isNil,
  values,
} from "lodash";
import * as ACTIONS from "../actionTypes";
import { PENDING } from "../saveStatus";
import { PLANNER } from "../pageModes";

import updateModelWithConstraint from "./updateModelWithConstraint";
import updateModelWithNewCellValue from "./updateModelWithNewCellValue";
import updateModelWithNewCellSaveStatus from "./updateModelWithNewCellSaveStatus";
import clearForecastOverTargetWarning from "./clearForecastOverTargetWarning";
import updateModelWithNewRole from "./updateModelWithNewRole";
import updateModelWithNewTeam from "./updateModelWithNewTeam";
import updateModelWithRenamedTeam from "./updateModelWithRenamedTeam";
import updateModelWithHiddenTeam from "./updateModelWithHiddenTeam";
import updateModelWithGroupObjectives from "./updateModelWithGroupObjectives";
import updateModelWithObjectivesVerified from "./updateModelWithObjectivesVerified";
import updateConstraintSaveStatus from "./updateConstraintSaveStatus";
import updateApproverCellNotifications from "./updateApproverCellNotifications";
import buildInitialModel from "./buildInitialModel";
import updateModelWithRealTimeMemberFTE from "./updateModelWithRealTimeMemberFTE";
import updateModelWithNewTags from "./updateModelWithNewTags";
import updateModelWithGroupTags from "./updateModelWithGroupTags";
import updateModelWithBaselineDiff from "./updateModelWithBaselineDiff";
import updateIntersectingFteSumForDateRanges from "./updateIntersectingFteSumForDateRanges";
import addEphemeralIntersectionSupplyLineItems from "./addEphemeralIntersectionSupplyLineItems";
import updateModelWithDemandRequests from "./updateModelWithDemandRequests";
import updateModelWithDemandRequestRow from "./updateModelWithDemandRequestRow";

import { hashCell } from "./util";

/**
 * Cells the user has edited but not yet saved.
 *
 * A rebuild reconstructs every cell from server data, so without carrying these
 * across, a query resolving under someone's typing silently discards it - and
 * with it the PENDING status the save button is enabled by, which leaves the
 * edit stranded: visible in the input (react-number-format holds its own DOM
 * state) but unsaveable, with no way back other than a reload.
 */
const getPendingCellEdits = (model) =>
  filter(
    values(get(model, "lookups.cellLookup")),
    // Clearing an input is a pending edit too - useSaveFteForCell dispatches a
    // null on blur - so this cannot filter on the value being set, or a cleared
    // field stays stranded in exactly the way the rest of this fixes. What is
    // filtered out is a cell left PENDING by the build itself, prepopulated
    // from forecast: nothing was typed, and the rebuild derives it again
    // identically. A user edit clears prepopulatedValue, so it survives here.
    (cell) => cell.valueSaveStatus === PENDING && isNil(cell.prepopulatedValue)
  );

// USER_FORECAST_INPUT_UPDATE turns this off on demand-request demand/source
// pages so later cells stay empty. Replay has to use the same rule or a
// query refresh prefills them again.
const shouldRecalcForecastDefaults = ({
  pageMode,
  enableDemandRequest,
  rootGroup,
}) =>
  pageMode !== PLANNER &&
  !(enableDemandRequest && (rootGroup?.isDemand || rootGroup?.isSource));

const fullReload = (
  state,
  {
    viewMode,
    pageMode,
    data,
    intersectingFteData,
    groupDiffData,
    shouldHash,
    userRole,
  }
) => {
  const resolvedUserRole = userRole ?? state.userRole;
  const {
    rootGroup: stateRootGroup,
    groupTypes,
    hideHiddenTeams,
    showObjectives,
    approvalReasons,
    isBudgetEnabled,
    useDeltaForBudget,
    overBudgetLimit,
    underBudgetLimit,
    isRealtimeFteMode,
    useRealtimeChangeOnly,
    useRealtimeFteDelta,
    prefillWithIntersectingFte,
    enableDemandRequest,
    enableDemandRollupPlanning,
    enableSupplyForecastTargets,
  } = state;
  const rootGroup =
    resolvedUserRole === stateRootGroup?.userRole
      ? stateRootGroup
      : { ...stateRootGroup, userRole: resolvedUserRole };
  const model = buildInitialModel({
    mainQueryData: data,
    viewMode,
    pageMode,
    rootGroup,
    groupTypes,
    approvalReasons,
    hideHiddenTeams,
    showObjectives,
    shouldHash: shouldHash && (!intersectingFteData || !groupDiffData),
    isBudgetEnabled,
    useDeltaForBudget,
    overBudgetLimit,
    underBudgetLimit,
    isRealtimeFteMode,
    useRealtimeChangeOnly,
    useRealtimeFteDelta,
    enableDemandRequest,
    enableDemandRollupPlanning,
    enableSupplyForecastTargets,
  });
  let newState = {
    ...state,
    rootGroup,
    userRole: resolvedUserRole,
    staticModel: model,
    dynamicModel: cloneDeep(model),
  };
  if (intersectingFteData) {
    newState = updateIntersectingFteSumForDateRanges(newState, {
      dataForRanges: intersectingFteData,
      prefillWithIntersectingFte,
      shouldHash: !groupDiffData,
    });
  }
  if (groupDiffData) {
    newState.dynamicModel = updateModelWithBaselineDiff(
      newState.dynamicModel,
      groupDiffData,
      {
        rootGroup,
        groupTypes,
        isBudgetEnabled,
        showObjectives,
        useDeltaForBudget,
        isRealtimeFteMode,
        useRealtimeChangeOnly,
        useRealtimeFteDelta,
        hideHiddenTeams,
      }
    );
  }

  // Replayed through updateModelWithNewCellValue rather than assigned straight
  // onto the cell, so the deltas, sum totals and notifications the edit feeds
  // are recalculated the same way they were when it was first typed. That
  // includes recalcDefaults, which has to match the dispatch the edit came from
  // - REQUEST_INPUT_UPDATE and demand-request forecast edits turn it off.
  // Cells that no longer exist in the rebuilt model are dropped on the floor
  // by design.
  forEach(getPendingCellEdits(state.dynamicModel), ({ id, value }) => {
    newState.dynamicModel = updateModelWithNewCellValue({
      forecastModel: newState.dynamicModel,
      cellId: id,
      newValue: value,
      recalcDefaults: shouldRecalcForecastDefaults({
        pageMode,
        enableDemandRequest,
        rootGroup,
      }),
      rootGroup,
      isBudgetEnabled,
      useDeltaForBudget,
      overBudgetLimit,
      underBudgetLimit,
      isRealtimeFteMode,
      useRealtimeChangeOnly,
      useRealtimeFteDelta,
      prefillWithIntersectingFte,
    });
  });

  return newState;
};

export default (state, action) => {
  switch (action.type) {
    case ACTIONS.MAIN_QUERY_COMPLETE: {
      const {
        data,
        previousData,
        intersectingFteData,
        groupDiffData,
        viewMode,
        pageMode,
        shouldHash,
        userRole,
      } = action;
      const roleChanged = Boolean(userRole) && userRole !== state.userRole;
      if (data && (!isEqual(data, previousData) || roleChanged)) {
        return fullReload(state, {
          viewMode,
          pageMode,
          shouldHash,
          data,
          intersectingFteData,
          groupDiffData,
          userRole,
        });
      }
      return state;
    }
    case ACTIONS.SET_VIEW_MODE: {
      const { data, intersectingFteData, groupDiffData, viewMode, pageMode } =
        action;
      // HELP-10497: avoid fullReload when main query data is null (e.g. after submit, before refetch)
      if (data == null) {
        return state;
      }
      return fullReload(state, {
        viewMode,
        pageMode,
        shouldHash: true,
        data,
        intersectingFteData,
        groupDiffData,
      });
    }
    case ACTIONS.SECOND_QUERY_COMPLETE: {
      return {
        ...state,
        dynamicModel: updateApproverCellNotifications(
          state.dynamicModel,
          action
        ),
      };
    }
    case ACTIONS.BASELINE_DIFF_QUERY_COMPLETE: {
      const {
        data,
        intersectingFteData,
        viewMode,
        pageMode,
        groupDiffData,
        previousGroupDiffData,
      } = action;
      // HELP-10497: avoid fullReload when main query data is null
      if (data == null) {
        return state;
      }
      if (groupDiffData && !isEqual(groupDiffData, previousGroupDiffData)) {
        return fullReload(state, {
          viewMode,
          pageMode,
          data,
          intersectingFteData,
          groupDiffData,
          shouldHash: true,
        });
      }
      return state;
    }

    case ACTIONS.USER_CONSTRAINT_INPUT_UPDATE: {
      const { cellId, constraint } = action;
      return {
        ...state,
        dynamicModel: updateModelWithConstraint(
          state.dynamicModel,
          cellId,
          constraint
        ),
      };
    }
    case ACTIONS.USER_FORECAST_INPUT_UPDATE: {
      const {
        dynamicModel,
        rootGroup,
        isBudgetEnabled,
        overBudgetLimit,
        underBudgetLimit,
        enableDemandRequest,
      } = state;
      const { cellId, value, prefillWithIntersectingFte } = action;
      return {
        ...state,
        dynamicModel: updateModelWithNewCellValue({
          forecastModel: dynamicModel,
          cellId,
          newValue: value,
          rootGroup,
          isBudgetEnabled,
          overBudgetLimit,
          underBudgetLimit,
          prefillWithIntersectingFte,
          recalcDefaults: shouldRecalcForecastDefaults({
            enableDemandRequest,
            rootGroup,
          }),
        }),
      };
    }
    case ACTIONS.REQUEST_INPUT_UPDATE: {
      const {
        dynamicModel,
        rootGroup,
        isBudgetEnabled,
        useDeltaForBudget,
        overBudgetLimit,
        underBudgetLimit,
        isRealtimeFteMode,
        useRealtimeChangeOnly,
        useRealtimeFteDelta,
      } = state;
      const { cellId, value, valueSaveStatus } = action;
      return {
        ...state,
        dynamicModel: updateModelWithNewCellValue({
          forecastModel: dynamicModel,
          cellId,
          newValue: value,
          rootGroup,
          recalcDefaults: false,
          valueSaveStatus,
          isBudgetEnabled,
          useDeltaForBudget,
          overBudgetLimit,
          underBudgetLimit,
          isRealtimeFteMode,
          useRealtimeChangeOnly,
          useRealtimeFteDelta,
        }),
      };
    }
    case ACTIONS.REALTIME_CURRENT_MEMBER_FTE_UPDATE: {
      return updateModelWithRealTimeMemberFTE(state, action);
    }
    case ACTIONS.APPROVE_INPUT_UPDATE: {
      const { cellId, value } = action;
      return {
        ...state,
        dynamicModel: updateModelWithNewCellValue({
          forecastModel: state.dynamicModel,
          cellId,
          newValue: value,
          rootGroup: state.rootGroup,
          recalcDefaults: false,
        }),
      };
    }
    case ACTIONS.USER_SKILLS_INPUT_UPDATE: {
      const { cellId, skills } = action;
      const {
        dynamicModel: {
          lookups: { cellLookup },
        },
      } = state;
      const cell = cellLookup[cellId];

      if (!cell || skills === cell.skills) {
        return state;
      }

      cell.skills = skills;
      cell.skillsSaveStatus = PENDING;
      hashCell(cell, { rehashParents: true });
      return {
        ...state,
      };
    }
    case ACTIONS.CELL_VALUE_SAVE_STATUS_UPDATE: {
      const {
        cellId,
        status,
        macroAllocation,
        revertedValue,
        failureMessage,
        prefillWithIntersectingFte,
      } = action;
      const {
        approvalReasons,
        rootGroup,
        isBudgetEnabled,
        useDeltaForBudget,
        overBudgetLimit,
        underBudgetLimit,
      } = state;
      return {
        ...state,
        dynamicModel: updateModelWithNewCellSaveStatus({
          dynamicModel: state.dynamicModel,
          cellId,
          cellStatusFieldName: "valueSaveStatus",
          newStatus: status,
          newMacroAllocation: macroAllocation,
          approvalReasons,
          rootGroup,
          revertedValue,
          failureMessage,
          prefillWithIntersectingFte,
          isBudgetEnabled,
          useDeltaForBudget,
          overBudgetLimit,
          underBudgetLimit,
          recalcDefaults: shouldRecalcForecastDefaults({
            pageMode: state.pageMode,
            enableDemandRequest: state.enableDemandRequest,
            rootGroup: state.rootGroup,
          }),
        }),
      };
    }
    case ACTIONS.CLEAR_FORECAST_OVER_TARGET_WARNING: {
      const { cellId } = action;
      return {
        ...state,
        dynamicModel: clearForecastOverTargetWarning(
          state.dynamicModel,
          cellId
        ),
      };
    }
    case ACTIONS.CONSTRAINT_SAVE_STATUS_UPDATE: {
      const { cellId, status, constraint } = action;
      return {
        ...state,
        dynamicModel: updateConstraintSaveStatus({
          forecastModel: state.dynamicModel,
          cellId,
          status,
          savedConstraint: constraint,
        }),
      };
    }
    case ACTIONS.SKILLS_SAVE_STATUS_UPDATE: {
      const { cellId, status, macroAllocation } = action;
      const { approvalReasons, rootGroup } = state;
      return {
        ...state,
        dynamicModel: updateModelWithNewCellSaveStatus({
          dynamicModel: state.dynamicModel,
          cellId,
          cellStatusFieldName: "skillsSaveStatus",
          newStatus: status,
          newMacroAllocation: macroAllocation,
          approvalReasons,
          rootGroup,
        }),
      };
    }
    case ACTIONS.ADD_NEW_EXISTING_ROLE:
    case ACTIONS.USER_ADD_ROLE: {
      const {
        role,
        supplyRoot,
        sourceGroupLookup,
        targetGroupId,
        pageMode,
        macroAllocationType,
        targetView,
        callback,
        shouldSortLineItems,
      } = action;
      const isDemandRequestEnabled =
        state.enableDemandRequest &&
        (state.rootGroup.isDemand || state.rootGroup.isSource);
      return {
        ...state,
        dynamicModel: updateModelWithNewRole({
          forecastModel: state.dynamicModel,
          role,
          supplyRoot,
          sourceGroupLookup,
          targetGroupId,
          pageMode,
          macroAllocationType,
          isRequestor: !state.rootGroup.isSource,
          rootGroup: state.rootGroup,
          groupTypes: state.groupTypes,
          targetView,
          callback,
          isBudgetEnabled: state.isBudgetEnabled,
          useDeltaForBudget: state.useDeltaForBudget,
          shouldSortLineItems,
          enableDemandRequest: isDemandRequestEnabled,
        }),
      };
    }
    case ACTIONS.USER_ADD_NEW_TEAM: {
      const { team } = action;
      const { showObjectives } = state;
      return {
        ...state,
        dynamicModel: updateModelWithNewTeam(
          state.dynamicModel,
          team,
          state.groupTypes[team.type],
          showObjectives
        ),
      };
    }
    case ACTIONS.TEAM_HIDDEN: {
      const { groupId } = action;
      return {
        ...state,
        dynamicModel: updateModelWithHiddenTeam(state.dynamicModel, groupId),
      };
    }
    case ACTIONS.TEAM_RENAMED: {
      const { groupId, name } = action;
      return {
        ...state,
        dynamicModel: updateModelWithRenamedTeam(
          state.dynamicModel,
          groupId,
          name
        ),
      };
    }
    case ACTIONS.LINKED_OBJECTIVES_UPDATE: {
      const { groupId, linkedStrategies, objectiveIds } = action;
      return {
        ...state,
        dynamicModel: updateModelWithGroupObjectives(
          state.dynamicModel,
          groupId,
          linkedStrategies,
          objectiveIds
        ),
      };
    }
    case ACTIONS.VERIFY_LINKED_OBJECTIVES: {
      const { groupId } = action;
      return {
        ...state,
        dynamicModel: updateModelWithObjectivesVerified(
          state.dynamicModel,
          groupId
        ),
      };
    }
    case ACTIONS.SUBMIT_FORECAST_COMPLETE: {
      const { forecastSubmittedFor, forecastSubmittedAt } = action;
      return {
        ...state,
        rootGroup: {
          ...state.rootGroup,
          forecastSubmittedFor,
          forecastSubmittedAt,
        },
      };
    }
    case ACTIONS.OPEN_SKILLS: {
      const { cellId, at } = action;
      const {
        dynamicModel: {
          lookups: { cellLookup },
        },
      } = state;
      const cell = cellLookup[cellId];

      if (!cell || cell.isSkillsOpen === at) {
        return state;
      }

      cell.isSkillsOpen = at;
      hashCell(cell, { rehashParents: true });
      return {
        ...state,
      };
    }
    case ACTIONS.UPDATE_GROUPING_TAGS: {
      return updateModelWithNewTags(state, action);
    }
    case ACTIONS.GROUP_TAGS_QUERY_COMPLETE: {
      return updateModelWithGroupTags(state, action);
    }
    case ACTIONS.UPDATE_DATE_RANGE_TOTAL_FTE: {
      const { prefillWithIntersectingFte } = state;
      const { dataForRanges, previousDataForRanges } = action;

      if (dataForRanges && !isEqual(dataForRanges, previousDataForRanges)) {
        return updateIntersectingFteSumForDateRanges(state, {
          ...action,
          prefillWithIntersectingFte,
          shouldHash: true,
        });
      }
      return state;
    }
    case ACTIONS.ADD_EPHEMERAL_INTERSECTION_SUPPLY_ROWS: {
      return addEphemeralIntersectionSupplyLineItems(state, action);
    }
    case ACTIONS.DEMAND_REQUESTS_QUERY_COMPLETE: {
      return updateModelWithDemandRequests(state, action);
    }
    case ACTIONS.DEMAND_REQUEST_ROW_QUERY_COMPLETE: {
      return updateModelWithDemandRequestRow(state, action);
    }
    default:
      return state;
  }
};
